import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { type JSX, useContext } from 'react';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { useAtom } from 'ximple';
import { formatCurrencyNoDecimalsIfPossible, useLocale } from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { tzdate } from '@repo/tzdate';
import Button from 'src/components/common/timeslots-buttons/button/Button';
import TextInput from 'src/components/common/timeslots-fields/inputs/text-input/TextInput';
import Payment from 'src/components/domain/payment/Payment';
import MemberIdentityHeader from './MemberIdentityHeader';
import MemberPageButton from './MemberPageButton';
import { resolveNativeText } from './nativeText';
import { useMemberCardPurchase } from './useMemberCardPurchase';

type MemberCardPurchaseProps = {
    preselectedProductId?: string;
    allowedProductIds?: string[];
};

// Self-contained native member-card buy flow: select → (pay) → confirmation. Renders nothing
// unless the tenant runs native membership. The member is authenticated via the login modal.
export default function MemberCardPurchase(props: MemberCardPurchaseProps): JSX.Element | null {
    const { t, locale, dayjsLocale } = useLocale();
    const theme = useTheme();
    const [currency] = useAtom(currencyAtom);
    const purchase = useMemberCardPurchase(props.preselectedProductId, props.allowedProductIds);
    const mountPoint = useContext(MountPointContext);

    if (!purchase.isNative) return null;

    const containerSx = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.paper,
        borderRadius: `${theme.shape.borderRadius}px`,
        border: `1px solid ${theme.palette.divider}`,
    } as const;

    if (purchase.step === 'pay' && purchase.paymentInfo) {
        return (
            <Box sx={containerSx}>
                <Payment
                    paymentInfo={purchase.paymentInfo}
                    onPaymentCancelled={purchase.onPaymentCancelled}
                    onPaymentCompleted={purchase.onPaymentCompleted}
                />
            </Box>
        );
    }

    if (purchase.step === 'done') {
        return (
            <Box sx={containerSx}>
                <Typography variant="h3">{t.member_card_purchased}</Typography>
                <Typography>{t.booking_details_in_email}</Typography>
                <MemberPageButton />
                <Button variant="outlined" text={t.continue_shopping} onClick={purchase.reset} />
            </Box>
        );
    }

    const price = (value: number) => formatCurrencyNoDecimalsIfPossible(locale, currency, value);

    return (
        <Box sx={containerSx}>
            {purchase.isLoggedIn && purchase.member && (
                <MemberIdentityHeader
                    label={t.loggedInAs}
                    name={`${purchase.member.first_name} ${purchase.member.last_name}`.trim()}
                    email={purchase.member.email}
                    logoutText={t.logout}
                    onLogout={() => void purchase.logout()}
                />
            )}

            {purchase.isLoadingProducts && <CircularProgress size={24} />}

            {purchase.products.map((product) => {
                const selected = product.id === purchase.selectedProductId;
                const description = resolveNativeText(product.description, locale);
                return (
                    <Box
                        key={product.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        onClick={() => purchase.setSelectedProductId(product.id)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                purchase.setSelectedProductId(product.id);
                            }
                        }}
                        sx={{
                            cursor: 'pointer',
                            padding: theme.spacing(1.5),
                            borderRadius: `${theme.shape.borderRadius}px`,
                            border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(0.5),
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: theme.spacing(1),
                            }}
                        >
                            <Typography sx={{ fontWeight: 'bold' }}>
                                {resolveNativeText(product.name, locale)}
                            </Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>
                                {price(product.price)}
                            </Typography>
                        </Box>
                        {description && (
                            <Typography
                                variant="body2"
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                );
            })}

            {purchase.isLoggedIn &&
                purchase.selectedProduct &&
                purchase.selectedProduct.allowsDeferredStart && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                        <DatePicker
                            label={t.start_date}
                            value={
                                purchase.startDate
                                    ? tzdate(purchase.startDate, false)
                                          .forceLocalTz()
                                          .getDayjsDate()
                                    : null
                            }
                            onChange={(value) =>
                                purchase.setStartDate(
                                    value ? tzdate(value).format('YYYY-MM-DD') : '',
                                )
                            }
                            minDate={tzdate().forceLocalTz().getDayjsDate()}
                            maxDate={
                                purchase.selectedProduct.salesWindow.end
                                    ? tzdate(purchase.selectedProduct.salesWindow.end, false)
                                          .forceLocalTz()
                                          .getDayjsDate()
                                    : undefined
                            }
                            slotProps={{
                                textField: { size: 'small', fullWidth: true },
                                // Render the calendar inside the widget's shadow DOM.
                                popper: { container: mountPoint.popover },
                                dialog: { container: mountPoint.popover },
                            }}
                        />
                    </LocalizationProvider>
                )}

            {purchase.needsPhone && (
                <TextInput
                    label={t.phone_number}
                    value={purchase.phone}
                    onChange={purchase.setPhone}
                    autoComplete="tel"
                    required
                />
            )}

            {purchase.hasError && (
                <Typography role="alert" sx={{ color: theme.palette.error.main }}>
                    {t.error_occurred_when_creating_reservation}
                </Typography>
            )}

            {purchase.isLoggedIn ? (
                <Button
                    text={
                        purchase.selectedProduct
                            ? `${t.buyMembership} · ${price(purchase.selectedProduct.price)}`
                            : t.buyMembership
                    }
                    onClick={() => void purchase.submit()}
                    disabled={!purchase.canSubmit}
                />
            ) : (
                <>
                    <Typography>{t.pleaseLogInToPurchase}</Typography>
                    <Button text={t.login} onClick={() => void purchase.submit()} />
                </>
            )}
        </Box>
    );
}
